// FalModels page — browser for the fal.ai reference catalog (PX-94).
//
// Separate from the curated `Catalog` page (which manages `models`
// rows that photoshoots FK into). This one mirrors the full public
// fal.ai catalog (~1266 entries) so we can pick candidates before
// promoting them into Catalog/Models.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import { TabSwitcher, Toggle } from '@/components/FormControls'
import { useAuth } from '@/lib/auth'
import { hasPerm } from '@/lib/perms'
import { PERM } from '@/types/api'
import type {
  AdminModelResponse,
  FalModelFacets,
  FalModelListResponse,
  FalModelSummary,
} from '@/types/api'

const PAGE_LIMIT = 50

interface Filters {
  category: string
  model_lab: string
  q: string
  deprecated: boolean
}

const EMPTY_FILTERS: Filters = {
  category: '',
  model_lab: '',
  q: '',
  deprecated: false,
}

function formatPricing(item: FalModelSummary): string {
  if (item.pricing_unit && item.pricing_amount_usd) {
    const amount = Number(item.pricing_amount_usd)
    const unit = item.pricing_unit.replace('per_', 'per ').replace('_', ' ')
    return `$${amount.toFixed(amount < 0.01 ? 4 : amount < 1 ? 3 : 2)} ${unit}`
  }
  if (item.pricing_raw_md) return '—'  // raw md exists but not parsed
  return '—'
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export function FalModelsPage() {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const canWrite = hasPerm(profile, PERM.fal_models_write)
  // PX-477: promoting a fal model into the curated catalog writes a `models`
  // row, so it's gated on models.write (not the fal-catalog approve flag).
  const canAddToCatalog = hasPerm(profile, PERM.models_write)
  // Per-row feedback for the add-to-catalog action (keyed by endpoint_id).
  const [catalogFeedback, setCatalogFeedback] = useState<
    Record<string, { kind: 'success' | 'error'; message: string }>
  >({})
  // PX-434: All = every catalog model; Approved = curator-approved only.
  const [tab, setTab] = useState<'all' | 'approved'>('all')
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [offset, setOffset] = useState(0)

  const queryKey = useMemo(
    () => ['admin', 'fal-models', { ...filters, tab, offset }] as const,
    [filters, tab, offset],
  )

  // Facets drive the Category dropdown + Lab datalist — populated from
  // DISTINCT values in `fal_models` so a new category surfaced by the
  // weekly sync (training-images, audio-to-audio, …) shows up without
  // a client release. Cached for the whole session — catalog changes
  // are weekly at most.
  const facetsQuery = useQuery({
    queryKey: ['admin', 'fal-models', 'facets'] as const,
    queryFn: () => request<FalModelFacets>('/admin/fal-models/facets'),
    staleTime: Infinity,
  })

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      request<FalModelListResponse>('/admin/fal-models', {
        query: {
          category: filters.category || undefined,
          model_lab: filters.model_lab || undefined,
          q: filters.q || undefined,
          deprecated: filters.deprecated ? 'true' : undefined,
          approved: tab === 'approved' ? 'true' : undefined,
          limit: PAGE_LIMIT,
          offset,
        },
      }),
    staleTime: 5 * 60_000,  // 5 min — catalog refreshes weekly
  })

  const approveMutation = useMutation({
    mutationFn: ({ endpointId, approved }: { endpointId: string; approved: boolean }) =>
      request<FalModelSummary>(`/admin/fal-models/${endpointId}/approve`, {
        method: 'PATCH',
        body: { approved },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'fal-models'] }),
  })

  // PX-477: promote a fal model into the curated `models` catalog. The
  // endpoint_id carries slashes — it rides the path the same way `approve`
  // does. The created model lands DISABLED with a default cost, so the
  // operator finishes setup on the Catalog → Models tab.
  const addToCatalogMutation = useMutation({
    mutationFn: (endpointId: string) =>
      request<AdminModelResponse>(`/admin/fal-models/${endpointId}/add-to-catalog`, {
        method: 'POST',
      }),
    onSuccess: (model, endpointId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'models'] })
      setCatalogFeedback((prev) => ({
        ...prev,
        [endpointId]: {
          kind: 'success',
          message: `Added "${model.slug}" to catalog (disabled — set cost & enable it).`,
        },
      }))
    },
    onError: (err, endpointId) => {
      const message =
        err instanceof APIError
          ? err.status === 409
            ? 'Already in the catalog.'
            : err.message
          : 'Failed to add to catalog.'
      setCatalogFeedback((prev) => ({
        ...prev,
        [endpointId]: { kind: 'error', message },
      }))
    },
  })

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setOffset(0)  // filter change resets pagination
  }

  const items = listQuery.data?.items ?? []
  const total = listQuery.data?.total ?? 0
  const hasMore = listQuery.data?.has_more ?? false

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">fal.ai Catalog</h1>
        <p className="text-sm text-ink-400">
          Reference mirror of fal.ai models — pick candidates for new photoshoots.
          Populated by <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">scripts/sync_fal_models.py</code>.
        </p>
      </header>

      <TabSwitcher
        tabs={[{ id: 'all', label: 'All' }, { id: 'approved', label: 'Approved' }] as const}
        current={tab}
        onChange={(id) => { setTab(id); setOffset(0) }}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-ink-400">
          Category
          <select
            value={filters.category}
            onChange={(e) => updateFilter('category', e.target.value)}
            className="rounded-md border border-ink-200 px-3 py-2 text-sm text-ink-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          >
            <option value="">All</option>
            {(facetsQuery.data?.categories ?? []).map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-ink-400">
          Lab
          {/*
            Combobox: native `<input list="…">` + `<datalist>` gives the
            user a dropdown of known labs to pick from AND the freedom
            to type a value that isn't in the list (filter still does
            an exact-match server-side). Same pattern Apple's design
            recommendation calls for in admin tooling.
          */}
          <input
            type="text"
            list="fal-lab-options"
            placeholder="Google, OpenAI, …"
            value={filters.model_lab}
            onChange={(e) => updateFilter('model_lab', e.target.value)}
            className="w-40 rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <datalist id="fal-lab-options">
            {(facetsQuery.data?.model_labs ?? []).map((lab) => (
              <option key={lab} value={lab} />
            ))}
          </datalist>
        </label>

        <label className="flex flex-1 flex-col gap-1 text-xs uppercase tracking-wide text-ink-400">
          Search
          <input
            type="search"
            placeholder="Title, description, endpoint id…"
            value={filters.q}
            onChange={(e) => updateFilter('q', e.target.value)}
            className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </label>

        <label className="flex items-center gap-2 self-end text-sm text-ink-700">
          <input
            type="checkbox"
            checked={filters.deprecated}
            onChange={(e) => updateFilter('deprecated', e.target.checked)}
            className="h-4 w-4 rounded border-ink-300 text-brand-500 focus:ring-brand-200"
          />
          Show deprecated
        </label>
      </div>

      <div className="mb-3 text-sm text-ink-500">
        {listQuery.isLoading ? 'Loading…' : `${total} model${total === 1 ? '' : 's'} match`}
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="w-16 px-2 py-3"></th>
              <th className="px-4 py-3">Model</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Lab</th>
              <th className="px-4 py-3">Pricing</th>
              <th className="px-4 py-3">Synced</th>
              <th className="px-4 py-3 text-right">Catalog</th>
              <th className="px-4 py-3 text-right">Approved</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.endpoint_id} className="border-b border-ink-50 last:border-b-0 hover:bg-ink-50">
                <td className="px-2 py-2">
                  {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt=""
                      loading="lazy"
                      className="h-12 w-12 rounded-md object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-md bg-ink-100" />
                  )}
                </td>
                <td className="px-4 py-2">
                  <Link
                    to={`/fal-models/${item.endpoint_id}`}
                    className="font-medium text-brand-600 hover:underline"
                  >
                    {item.title}
                  </Link>
                  <div className="text-xs text-ink-400">{item.endpoint_id}</div>
                  {item.deprecated && (
                    <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-red-700">
                      deprecated
                    </span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {item.category && (
                    <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">
                      {item.category}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-ink-700">{item.model_lab ?? '—'}</td>
                <td className="px-4 py-2 font-mono text-xs text-ink-700">{formatPricing(item)}</td>
                <td className="px-4 py-2 text-xs text-ink-500">{formatDate(item.last_synced_at)}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-col items-end gap-1">
                    <button
                      type="button"
                      disabled={
                        !canAddToCatalog ||
                        (addToCatalogMutation.isPending &&
                          addToCatalogMutation.variables === item.endpoint_id)
                      }
                      onClick={() => addToCatalogMutation.mutate(item.endpoint_id)}
                      className="rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-brand-600 transition hover:border-brand-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {addToCatalogMutation.isPending &&
                      addToCatalogMutation.variables === item.endpoint_id
                        ? 'Adding…'
                        : 'Add to catalog'}
                    </button>
                    {catalogFeedback[item.endpoint_id] && (
                      <span
                        className={`text-right text-[10px] ${
                          catalogFeedback[item.endpoint_id].kind === 'success'
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }`}
                      >
                        {catalogFeedback[item.endpoint_id].message}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex justify-end">
                    <Toggle
                      checked={item.approved}
                      disabled={!canWrite || approveMutation.isPending}
                      onChange={(v) => approveMutation.mutate({ endpointId: item.endpoint_id, approved: v })}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {!listQuery.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-ink-400">
                  No models match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-ink-500">
          Showing {offset + 1}–{offset + items.length} of {total}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={offset === 0 || listQuery.isFetching}
            onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!hasMore || listQuery.isFetching}
            onClick={() => setOffset(offset + PAGE_LIMIT)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}
